import { z } from 'zod';
import { claimFails } from './claims.js';
/**
 * Underwriter review (tracker task 61, decision 0037). After the business submits, an underwriter goes
 * through the answers: approves them, flags them for the insurer's own attention, or asks the business to
 * change them. Requested changes go back to the business together ("in remediation"); the business may
 * change only those answers, or reply to say why the answer stands, and sends the assessment back.
 *
 *   in_progress → submitted → in_review ⇄ in_remediation → decided (task 64)
 */
/** The business answers. In remediation, only the questions the insurer asked it to change. */
export const ANSWERING_STATUSES = [
    'in_progress',
    'in_remediation',
];
/** The insurer reviews. */
export const REVIEW_STATUSES = [
    'submitted',
    'in_review',
    'in_remediation',
];
/** Not finished: workflows and reminders still run. */
export const OPEN_STATUSES = [
    'in_progress',
    ...REVIEW_STATUSES,
];
export const takesAnswers = (s) => ANSWERING_STATUSES.includes(s);
export const inReview = (s) => REVIEW_STATUSES.includes(s);
export const isOpen = (s) => OPEN_STATUSES.includes(s);
export const REVIEW_ACTIONS = ['approve', 'flag', 'clear'];
const QuestionKey = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_.-]{0,63}$/);
const Note = z.string().trim().min(1).max(2000);
/**
 * One review decision on the answer the reviewer saw: `based_on` is its response id (null when there is no
 * answer), so a decision never lands on an answer that changed meanwhile.
 */
export const ReviewItem = z.strictObject({
    question: QuestionKey,
    action: z.enum(REVIEW_ACTIONS),
    note: Note.optional(),
    based_on: z.uuid().nullable(),
});
export const ReviewActions = z.strictObject({ items: z.array(ReviewItem).min(1).max(500) });
export const CreateChangeRequest = z.strictObject({ question: QuestionKey, message: Note });
export const ChangeRequestReply = z.strictObject({ text: Note });
/**
 * open: written by the insurer, not yet sent (the business can't see it). sent: with the business.
 * answered: the business sent the assessment back with a new answer or a reply. withdrawn: dropped.
 */
export const CHANGE_REQUEST_STATES = ['open', 'sent', 'answered', 'withdrawn'];
export const liveRequest = (s) => s === 'open' || s === 'sent';
/** The business changed the answer since the request, or replied to it. */
export const requestAddressed = (r, current) => (current !== null && current !== r.response_id) || !!r.reply;
export const REVIEW_STATES = ['to_review', 'changed', 'approved', 'flagged', 'change_requested', 'unanswered']; // prettier-ignore
export const REVIEW_STATE_LABEL = {
    to_review: 'To review',
    changed: 'Changed since review',
    approved: 'Approved',
    flagged: 'Flagged',
    change_requested: 'Change requested',
    unanswered: 'Not answered',
};
/**
 * Where one question stands in the review. A live change request wins. A request the business answered
 * after the latest review needs looking at again; otherwise the latest review counts only while it was
 * made on the current answer, and an answer that changed after a review needs looking at again.
 */
export function reviewState(inp) {
    const reqs = inp.requests ?? [];
    if (reqs.some((r) => liveRequest(r.state)))
        return 'change_requested';
    const r = inp.latest;
    if (reqs.some((q) => q.state === 'answered' && (!r || q.created_at > r.created_at)))
        return inp.current === null ? 'unanswered' : 'changed';
    if (r && r.action !== 'clear' && r.response_id === inp.current)
        return r.action === 'approve' ? 'approved' : 'flagged';
    if (inp.current === null)
        return 'unanswered';
    const reviewedBefore = !!r && r.action !== 'clear' && r.response_id !== inp.current;
    return reviewedBefore ? 'changed' : 'to_review';
}
/** Which answered questions fail a control, from the latest score's trace. Kill switches first. */
export function failingQuestions(trace) {
    const out = new Map();
    for (const c of trace?.controls ?? []) {
        if (c.status !== 'missing' && c.status !== 'partial')
            continue;
        for (const q of c.answered) {
            const list = out.get(q) ?? [];
            list.push({ control: c.control, name: c.name, kill_switch: c.kill_switch, status: c.status });
            out.set(q, list);
        }
    }
    for (const list of out.values())
        list.sort((a, b) => Number(b.kill_switch) - Number(a.kill_switch) || a.control.localeCompare(b.control)); // prettier-ignore
    return out;
}
export function reviewProgress(states) {
    const n = (s) => states.filter((x) => x === s).length;
    const p = {
        to_review: n('to_review'),
        changed: n('changed'),
        approved: n('approved'),
        flagged: n('flagged'),
        change_requested: n('change_requested'),
    };
    return {
        ...p,
        total: states.length - n('unanswered'),
        reviewed: p.approved + p.flagged + p.change_requested,
    };
}
export const REVIEW_FILTERS = ['needs_review', 'failing', 'change_requested', 'flagged', 'approved', 'all']; // prettier-ignore
export const REVIEW_FILTER_LABEL = {
    needs_review: 'Needs review',
    failing: 'Failing',
    change_requested: 'Change requested',
    flagged: 'Flagged',
    approved: 'Approved',
    all: 'All answers',
};
export function matchesFilter(f, row) {
    switch (f) {
        case 'needs_review':
            return row.state === 'to_review' || row.state === 'changed';
        case 'failing':
            // A failing control, or a claim check the documents don't back (task 62).
            return ((row.failing.length > 0 || (!!row.checks && claimFails(row.checks))) && row.state !== 'unanswered');
        case 'all':
            return row.state !== 'unanswered';
        default:
            return row.state === f;
    }
}
//# sourceMappingURL=review.js.map